// Client side of SPEC §3. The access token lives in memory only (never storage).
// The refresh token never reaches the browser; the server refreshes on our behalf.

export class SignedOutError extends Error {
  constructor() { super('signed out'); this.name = 'SignedOutError'; }
}

type Token = { accessToken: string; expiresAt: number };

/** Refresh this long before the token expires. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

export class Session {
  #token: Token | null = null;
  #refreshing: Promise<Token> | null = null;

  private readonly fetchFn: typeof fetch;
  private readonly now: () => number;

  constructor(fetchFn: typeof fetch = (...a) => fetch(...a), now: () => number = Date.now) {
    this.fetchFn = fetchFn;
    this.now = now;
  }

  /** Load the token for the current server session. Returns false when signed out. */
  async load(): Promise<boolean> {
    const res = await this.fetchFn('/auth/token', { credentials: 'same-origin' });
    if (res.status === 401) { this.#token = null; return false; }
    if (!res.ok) throw new Error(`/auth/token failed: ${res.status}`);
    this.#token = (await res.json()) as Token;
    return true;
  }

  get signedIn(): boolean { return this.#token !== null; }

  /** A usable access token, refreshed first if it is about to expire. */
  async accessToken(): Promise<string> {
    if (!this.#token) throw new SignedOutError();
    if (this.#token.expiresAt - this.now() < REFRESH_MARGIN_MS) await this.refresh();
    return this.#token!.accessToken;
  }

  /** Single-flight: concurrent callers share one refresh request. */
  async refresh(): Promise<void> {
    this.#refreshing ??= (async () => {
      try {
        const res = await this.fetchFn('/auth/refresh', { method: 'POST', credentials: 'same-origin' });
        if (!res.ok) { this.#token = null; throw new SignedOutError(); }
        return (this.#token = (await res.json()) as Token);
      } finally {
        this.#refreshing = null;
      }
    })();
    await this.#refreshing;
  }

  async signOut(): Promise<void> {
    this.#token = null;
    await this.fetchFn('/auth/logout', { method: 'POST', credentials: 'same-origin' });
  }
}

/** Full-page redirect (SPEC §3: no popup). `switchAccount` asks GitHub for the account picker. */
export function signIn({ switchAccount = false } = {}): void {
  location.assign(switchAccount ? '/auth/login?switch=1' : '/auth/login');
}
